import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { ArrowLeft, Download } from 'lucide-react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import { OwnerReportSummary } from '../../components/features/reports/OwnerReportSummary'
import { PropertyReservationsAccordion } from '../../components/features/reports/PropertyReservationsAccordion'
import { useOwnerReportDetails } from '../../hooks/useOwnerReportDetails'
import { exportOwnerReportToPDF } from '../../utils/pdf-export'

type PayoutReportsContext = {
  startDate: Date | null
  endDate: Date | null
  setStartDate: (date: Date | null) => void
  setEndDate: (date: Date | null) => void
}

export function OwnerReportDetailsPage() {
  const { ownerId } = useParams<{ ownerId: string }>()
  const { startDate, endDate } = useOutletContext<PayoutReportsContext>()
  const { payoutData, loading } = useOwnerReportDetails(
    ownerId || '',
    startDate,
    endDate
  )

  const toast = useToast()

  const handleExportPDF = async () => {
    if (!payoutData) return
    try {
      const fileName = await exportOwnerReportToPDF(
        payoutData,
        startDate,
        endDate
      )
      toast({
        title: 'PDF gerado com sucesso!',
        description: `Arquivo ${fileName} foi baixado.`,
        status: 'success',
        duration: 3000,
      })
    } catch (error) {
      toast({
        title: 'Erro ao gerar PDF',
        description:
          error instanceof Error ? error.message : 'Erro desconhecido',
        status: 'error',
        duration: 5000,
      })
    }
  }

  if (loading && !payoutData) {
    return (
      <VStack spacing={8} align="center" justify="center" minH="400px">
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text color="gray.600" fontSize="lg">
          Carregando dados do proprietário...
        </Text>
      </VStack>
    )
  }

  return (
    <VStack
      px={{ base: 2, md: 6 }}
      spacing={{ base: 2, md: 8 }}
      align="stretch"
    >
      <HStack justify="space-between" align="flex-start">
        <Box>
          <Button
            as={Link}
            to="/reports"
            leftIcon={<Icon as={ArrowLeft} />}
            variant="link"
            mb={4}
          >
            Voltar para Relatórios
          </Button>
          <Heading
            as="h1"
            size={{ base: 'lg', md: 'xl' }}
            color="gray.800"
            mb={2}
          >
            Relatório de {payoutData?.owner_name}
          </Heading>
          <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }}>
            Detalhamento financeiro por propriedade
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={Download} />}
          colorScheme="red"
          size={{ base: 'md', md: 'lg' }}
          onClick={handleExportPDF}
          isDisabled={!payoutData}
          isLoading={loading}
        >
          Exportar PDF
        </Button>
      </HStack>

      {payoutData && (
        <>
          <OwnerReportSummary reportData={payoutData} />
          <PropertyReservationsAccordion reportData={payoutData} />
        </>
      )}
    </VStack>
  )
}
